"use client";

import { Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PushNudgeMockRow as MockRowData } from "../lib/push-nudge-copy";

/**
 * The frosted bell. Sized by role: 56px when it is the subject of a sheet,
 * 44px when it sits beside a headline in a card.
 *
 * The idle sway is 2 degrees and runs on a CSS keyframe rather than a spring —
 * it is ambient, never in response to anything, so nothing needs to be able to
 * interrupt it. `motion-reduce` stops it outright.
 */
export function PushNudgeMedallion({
  size = "lg",
  className,
}: {
  size?: "lg" | "sm";
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-brand/12 text-brand backdrop-blur",
        "motion-safe:animate-[push-nudge-sway_4s_ease-in-out_infinite]",
        size === "lg" ? "h-14 w-14" : "h-11 w-11",
        className,
      )}
    >
      <Bell className={size === "lg" ? "h-7 w-7" : "h-5 w-5"} />
    </span>
  );
}

/**
 * A fake notification, shown so the ask is concrete.
 *
 * It states the app name and a real timestamp because a mock that looks like
 * marketing persuades nobody — the point is to show the exact thing they will
 * receive. Copy comes from push-nudge-copy.ts, which is pinned by test to
 * notifications the backend actually dispatches.
 */
export function PushNudgeMockRow({
  row,
  index = 0,
}: {
  row: MockRowData;
  index?: number;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-surface px-3.5 py-3 shadow-sm",
        "dark:border-glass-border-10 dark:bg-glass-bg-05",
        "motion-safe:animate-[push-nudge-row-in_0.42s_var(--ease-out-strong)_both]",
      )}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div className="flex items-center gap-1.5 text-[11px] text-text-tertiary">
        <span className="h-3 w-3 rounded-[4px] bg-brand" aria-hidden="true" />
        <span className="font-medium tracking-wide uppercase">Hiive</span>
        <span className="ml-auto tabular-nums">{row.timestamp}</span>
      </div>
      <p className="mt-1 text-[15px] leading-snug font-semibold text-text-primary">
        {row.title}
      </p>
      <p className="text-[14px] leading-snug text-text-secondary">{row.body}</p>
    </div>
  );
}

/** The scope-and-exit line. Never optional — §5 requires both halves. */
export function PushNudgeReassurance({ children }: { children: string }) {
  return (
    <p className="text-center text-[11px] leading-relaxed text-text-tertiary">
      {children}
    </p>
  );
}
